import React, { useState } from 'react';
import { Button, Form, FormGroup, FormText, Label, Input, Row, Col } from 'reactstrap';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import Select from 'react-select';
import {createOrganization} from '../../slices/organizations.slice';
import TimezonePicker from '../../components/TimezonePicker'
import CountrySelect from '../../components/CountrySelect';
import countries from '../Movies/countries.json';
import TierSwitch from '../../components/TierSwitch';
import ToggleSwitch from '../../components/ToggleSwitch';
import currencies, {getCurrencyInfo} from '../../utils/currencies';
import {deriveSiteUrl} from '../../utils/organization';
import useSavePrompt from '../../utils/useSavePrompt';
import SavePrompt from '../../components/SavePrompt'

const CreateOrganization = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const currencyList = currencies.map(({ value, label }) => ({ value, label: `${value} - ${label}`}));

    const [formData, setFormData] = useState({
        name: '',
        creditAmount: '',
        tier: 'mid',
        country: '',
        siteLocation: '',
        siteUrl: '',
        timezone: '',
        currency: '',
        isYacht: false,
    });
    const [originalData] = useState(formData)
    const [modalCountries, setModalCountries] = useState(false);
    const [modalSiteLocation, setModalSiteLocation] = useState(false);

    const { blocker, isDirty } = useSavePrompt(originalData, formData)

    const toggleModalCountries = () => setModalCountries(!modalCountries);
    const toggleModalSiteLocation = () => setModalSiteLocation(!modalSiteLocation);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            // The site URL follows the name as long as it still is the derived
            // one; a hand-written URL is never overwritten.
            ...(name === 'name' && (!prev.siteUrl || prev.siteUrl === deriveSiteUrl(prev.name))
                ? { siteUrl: deriveSiteUrl(value) }
                : {}),
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const promise = dispatch(createOrganization({
            token: auth.token,
            organizationBody: formData,
        })).unwrap();

        toast.promise(promise, {
            pending: 'Creating organization...',
            success: 'Organization created',
            error: 'Error creating the organization',
        });

        promise.then(({_id}) => {
            navigate(`/organizations/${_id}`);
        });
    };

    return (
        <div className="create-organization-view p-3 min-vh-100">
            <Row>
                <Col>
                    <NavLink to={`/organizations`} className="text-decoration-none">
                        <span className="fas fa-chevron-left me-2" /> Organizations
                    </NavLink>
                </Col>
            </Row>
            <h1>Create Organization</h1>
            <Form onSubmit={handleSubmit}>
                <FormGroup>
                    <Label for="name">Name</Label>
                    <Input
                        type="text"
                        name="name"
                        id="name"
                        placeholder="Organization Name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                </FormGroup>

                <FormGroup>
                    <Label for="siteUrl">Site URL</Label>
                    <Input
                        type="url"
                        name="siteUrl"
                        id="siteUrl"
                        placeholder="http://00000.site.belaircinema.com:8446/"
                        value={formData.siteUrl}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup>
                    <Label for="type">Territory</Label>
                    <CountrySelect
                        countries={countries.regions}
                        onChange={value => handleChange({ target: { name: 'country', value } })}
                        multiple={false}
                        name="country"
                        value={formData.country}
                        placeholder="Select territory"
                        modal={modalCountries}
                        toggle={toggleModalCountries}
                    />
                    <FormText color="">Controls which movie versions this site can order.</FormText>
                </FormGroup>

                <FormGroup>
                    <Label for="type">Site Location</Label>
                    <CountrySelect
                        countries={countries.regions}
                        onChange={value => handleChange({ target: { name: 'siteLocation', value } })}
                        multiple={false}
                        name="siteLocation"
                        value={formData.siteLocation}
                        placeholder="Select site location"
                        modal={modalSiteLocation}
                        toggle={toggleModalSiteLocation}
                    />
                    <FormText color="">Physical location only. Does not affect availability.</FormText>
                </FormGroup>

                <FormGroup>
                    <Label for="timezone">Timezone</Label>
                    <TimezonePicker
                        value={formData.timezone}
                        onChange={({value}) => handleChange({ target: { name: 'timezone', value } })}
                    />
                </FormGroup>

                <FormGroup>
                    <Label for="currency">Currency</Label>
                    <Select
                        options={currencyList}
                        value={getCurrencyInfo(formData.currency)}
                        classNamePrefix="timezone-select"
                        onChange={({value}) => handleChange({ target: { name: 'currency', value } })}
                    />
                </FormGroup>

                <FormGroup>
                    <Label for="creditAmount">Credit Amount</Label>
                    <Input
                        type="number"
                        name="creditAmount"
                        id="creditAmount"
                        placeholder="Credit Amount"
                        value={formData.creditAmount}
                        onChange={handleChange}
                    />
                </FormGroup>

                <FormGroup>
                    <Label for="type">Organization Type</Label>
                    <br/>
                    <TierSwitch value={formData.tier} onChange={handleChange} propName="tier" />
                </FormGroup>

                <FormGroup>
                    <Label for="type">Is yacht?</Label>
                    <ToggleSwitch
                        isOn={formData.isYacht}
                        onChange={value => handleChange({ target: { name: 'isYacht', value }  })}
                    />
                </FormGroup>

                <Button color="primary" outline type="submit">
                    {isDirty && <span className="unsaved-dot" />}
                    <span className="fas fa-save me-2" />
                    Create organization
                </Button>
                <SavePrompt blocker={blocker}/>
            </Form>
        </div>
    );
};

export default CreateOrganization;
